import {Account, AccountUser} from "@/lib/types/accounts.ts";
import {MonitoringServiceType, Service, ServiceNameAddressAndType, ServiceTag} from "@/lib/types/checks.ts";
import {ContactGroupBase} from "@/lib/types/contacts.ts";

import {AuditLog} from "./auditlogs";

export interface MaintenanceSchedule {
  id: number;
  name: string;
  schedule_type: MaintenanceScheduleType;
  rrule?: string;
  duration_minutes?: number;
  expire_after?: Date;
  is_active: boolean;

  starts_at: string;
  ends_at?: string;

  pause_checks_during_maintenance: boolean;
  tags: ServiceTag[];
  deleted_at?: Date;
  created_at: string;
  modified_at?: string;

  next_occurrence?: MaintenanceOccurrence;
  is_in_maintenance: boolean;
  notifications?: MaintenanceScheduleNotification[];
}

export interface MaintenanceScheduleWithServices extends MaintenanceSchedule {
  services: Service[];
}

export interface MaintenanceScheduleListItem extends MaintenanceSchedule {
  services_count: number;
  services_preview: ServiceNameAddressAndType[];
}

export type MaintenanceScheduleEditData = Omit<MaintenanceScheduleWithServices, "id"> & {
  id?: MaintenanceScheduleWithServices["id"];
};

export enum MaintenanceScheduleType {
  RRULE = "RRULE",
  MANUAL = "MANUAL",
  ONE_OFF = "ONE_OFF",
}

export enum MaintenanceScheduleState {
  PAUSED = "PAUSED",
  ACTIVE = "ACTIVE",
  IN_MAINTENANCE = "IN_MAINTENANCE",
  ENDED = "ENDED",
}

export enum MaintenanceOccurrenceStatus {
  SCHEDULED = "SCHEDULED",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
}

export interface MaintenanceOccurrence {
  id: number;
  schedule: MaintenanceScheduleWithServices;
  services: Service[];
  pause_checks_during_maintenance: boolean;
  starts_at: string;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
  recurrence_id: string;
  is_exception: boolean;
  exception_reason?: string;
  cancelled_at?: string | null;
}

export interface MaintenanceEvent {
  id: number;
  account: Account;
  service?: Service;
  schedule?: MaintenanceSchedule;
  occurrence?: MaintenanceOccurrence;
  event_type: MaintenanceEventType;
  created_at: string;
  user?: AccountUser;
  meta?: Record<string, unknown>;
  audit_log_entry?: AuditLog;
  services_at_creation: {id: number}[];
}

export interface MaintenanceAuditEventCheck {
  id: number;
  name: string;
  monitoring_service_type: MonitoringServiceType | null;
  is_deleted: boolean;
}

export enum MaintenanceEventType {
  ENTERED = "ENTERED",
  EXITED = "EXITED",
  CHECK_PAUSED = "CHECK_PAUSED",
  CHECK_RESUMED = "CHECK_RESUMED",
  SCHEDULE_CREATED = "SCHEDULE_CREATED",
  SCHEDULE_UPDATED = "SCHEDULE_UPDATED",
  SCHEDULE_DELETED = "SCHEDULE_DELETED",
  SCHEDULE_PAUSED = "SCHEDULE_PAUSED",
  SCHEDULE_RESUMED = "SCHEDULE_RESUMED",
  EXCEPTION_ADDED = "EXCEPTION_ADDED",
  OCCURRENCE_STARTED = "OCCURRENCE_STARTED",
  OCCURRENCE_COMPLETED = "OCCURRENCE_COMPLETED",
  OCCURRENCE_CANCELED = "OCCURRENCE_CANCELED",
  OCCURRENCE_REPLACED = "OCCURRENCE_REPLACED",
  NOTIFICATION_CREATED = "NOTIFICATION_CREATED",
  NOTIFICATION_UPDATED = "NOTIFICATION_UPDATED",
  NOTIFICATION_DELETED = "NOTIFICATION_DELETED",
  NOTIFICATION_SENT = "NOTIFICATION_SENT",
}

export enum NotificationEvent {
  START = "START",
  END = "END",
}

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  [NotificationEvent.START]: "start",
  [NotificationEvent.END]: "end",
};

export interface MaintenanceScheduleNotification {
  id: number;
  offset: number;
  event: string;
  contact_groups: ContactGroupBase[];
}
